/** The least a conversation has to be for the history menu to list it. Both the chat panel's
 *  records and the workflow builder's satisfy it. */
export interface PanelHistoryConversation {
  id: string;
  title: string | null;
  updatedAt: string;
  /** Absent on a surface with no sharing, which then has no "shared with workspace" group. */
  userId?: string;
}

export interface PanelHistoryGroup {
  heading: string;
  conversations: PanelHistoryConversation[];
}

const DAY_MS = 24 * 60 * 60 * 1000;

const byNewest = (a: PanelHistoryConversation, b: PanelHistoryConversation) =>
  new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();

/** How long ago a conversation was last touched, in the shortest form that is still true. */
export function formatConversationAge(
  updatedAt: string,
  now: number = Date.now()
): string {
  const elapsed = Math.max(0, now - new Date(updatedAt).getTime());
  const days = Math.floor(elapsed / DAY_MS);

  if (days < 1) {
    const hours = Math.floor(elapsed / (60 * 60 * 1000));

    return hours < 1 ? 'now' : `${hours}h`;
  }

  if (days < 7) return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;

  return `${Math.floor(days / 365)}y`;
}

/** Search over the history menu. An untitled conversation has nothing to match, so it drops out of
 *  a search but stays in the unfiltered list. */
export function filterConversationsByTitle<T extends PanelHistoryConversation>(
  conversations: readonly T[],
  query: string
): T[] {
  const trimmed = query.trim().toLowerCase();

  if (!trimmed) return [...conversations];

  return conversations.filter((conversation) =>
    (conversation.title ?? '').toLowerCase().includes(trimmed)
  );
}

/** Buckets conversations by recency, newest first, dropping empty buckets. */
export function groupConversationsByRecency(
  conversations: readonly PanelHistoryConversation[],
  now: number = Date.now()
): PanelHistoryGroup[] {
  const buckets = [
    { heading: 'This week', maxDays: 7 },
    { heading: '2 weeks ago', maxDays: 14 },
    { heading: '3 weeks ago', maxDays: 21 },
    { heading: 'Older', maxDays: Number.POSITIVE_INFINITY },
  ];

  const sorted = [...conversations].sort(byNewest);

  return buckets
    .map(({ heading, maxDays }, index) => {
      const minDays = index === 0 ? 0 : (buckets[index - 1]?.maxDays ?? 0);

      return {
        heading,
        conversations: sorted.filter((conversation) => {
          const days = Math.floor(
            Math.max(0, now - new Date(conversation.updatedAt).getTime()) /
              DAY_MS
          );

          return days >= minDays && days < maxDays;
        }),
      };
    })
    .filter((group) => group.conversations.length > 0);
}

/** The viewer's own conversations bucketed by recency, then one trailing group for conversations
 *  teammates shared with the workspace. A surface that reports no viewer has no shared group. */
export function groupConversationsForViewer(
  conversations: readonly PanelHistoryConversation[],
  viewerId: string | undefined,
  now: number = Date.now()
): PanelHistoryGroup[] {
  if (viewerId === undefined)
    return groupConversationsByRecency(conversations, now);

  const own = conversations.filter(
    (conversation) => conversation.userId === viewerId
  );
  const shared = conversations
    .filter((conversation) => conversation.userId !== viewerId)
    .sort(byNewest);

  return [
    ...groupConversationsByRecency(own, now),
    ...(shared.length > 0
      ? [{ heading: 'Shared with workspace', conversations: shared }]
      : []),
  ];
}
